import type { ChangeEvent, FormEvent } from 'react';
import type { ElementRef } from '../../../../lib/teact/teact';
import React, {
  memo, useEffect, useRef, useState,
} from '../../../../lib/teact/teact';

import type {
  ApiWalletTransactionAttachment,
  ApiWalletTransactionCategory,
  ApiWalletTransactionCounterparty,
  ApiWalletTransactionDraftAttachment,
} from '../../../../api/types';
import type { WalletSystemMetadata } from '../../../../util/walletSystemMetadata';
import type { AttachmentContentLoader } from './TransactionAttachmentPreview';

import buildClassName from '../../../../util/buildClassName';
import { logDebugError } from '../../../../util/logs';
import {
  notifyWalletMetadataDirectoryChanged, resolveAddressCounterparty,
} from '../../../../util/walletMetadataDirectory';
import {
  isWalletSystemCategory, isWalletSystemCounterparty,
} from '../../../../util/walletSystemMetadata';
import { callApiWithThrow } from '../../../../api';
import { getTransactionAttachmentFileType } from './transactionMetadataFiles';

import useLang from '../../../../hooks/useLang';
import useLastCallback from '../../../../hooks/useLastCallback';
import useMetadataAuthorization, { MetadataAuthorizationCancelled } from '../../../../hooks/useMetadataAuthorization';

import CounterpartyAvatar from '../../../common/CounterpartyAvatar';
import CounterpartyDetailsModal from '../../../common/CounterpartyDetailsModal';
import MetadataCategoryIcon from '../../../common/MetadataCategoryIcon';
import MetadataRecordEditor from '../../../common/MetadataRecordEditor';
import Dropdown from '../../../ui/Dropdown';
import Modal from '../../../ui/Modal';
import ModalHeader from '../../../ui/ModalHeader';
import Spinner from '../../../ui/Spinner';
import WithContextMenu from '../../../ui/WithContextMenu';
import TransactionAttachmentPreview from './TransactionAttachmentPreview';

import dropdownStyles from '../../../ui/Dropdown.module.scss';
import inputStyles from '../../../ui/Input.module.scss';
import styles from './TransactionMetadata.module.scss';

import fileIcons from '../../../../assets/transaction-file-icons.svg';

const MAX_ATTACHMENTS = 5;
const CREATE_RECORD_VALUE = 'create';
const EDIT_RECORD_VALUE = 'edit';
const OPEN_RECORD_VALUE = 'open';
const EMPTY_COUNTERPARTIES: ApiWalletTransactionCounterparty[] = [];
const MAX_ATTACHMENT_BYTES = 5 * 1024 * 1024;
const ATTACHMENT_ACCEPT = [
  'image/png', 'image/jpeg', 'image/webp', 'image/heic', 'image/heif', 'image/gif',
  'application/pdf', 'text/plain', 'text/csv', 'application/json',
  '.doc', '.docx', '.xls', '.xlsx',
].join(',');

type Props = {
  accountId?: string;
  transactionHash?: string;
  address?: string;
  counterparties?: ApiWalletTransactionCounterparty[];
  systemMetadata?: Partial<WalletSystemMetadata>;
  description: string;
  counterparty?: ApiWalletTransactionCounterparty;
  categoryId: string;
  categories: ApiWalletTransactionCategory[];
  attachments: ApiWalletTransactionAttachment[];
  localAttachments: ApiWalletTransactionDraftAttachment[];
  onDescriptionChange: (value: string) => void;
  onCategoryChange: (value: string) => void;
  onAddFiles: (files: File[]) => void;
  onRemoveAttachment: (attachmentId: string) => void;
  onRemoveLocalAttachment: (clientId: string) => void;
  onOpenAttachment: (attachment: ApiWalletTransactionAttachment) => void;
  onOpenLocalAttachment: (attachment: ApiWalletTransactionDraftAttachment) => void;
  onSaveAttachment: (attachment: ApiWalletTransactionAttachment) => void | Promise<void>;
  onSaveLocalAttachment: (attachment: ApiWalletTransactionDraftAttachment) => void | Promise<void>;
  onLoadAttachmentContent?: AttachmentContentLoader;
  attachmentPreviewRevision?: number;
};

export const TransactionMetadataEditor = memo(({
  accountId,
  transactionHash,
  address,
  counterparties = EMPTY_COUNTERPARTIES,
  systemMetadata,
  description,
  counterparty,
  categoryId,
  categories,
  attachments,
  localAttachments,
  onDescriptionChange,
  onCategoryChange,
  onAddFiles,
  onRemoveAttachment,
  onRemoveLocalAttachment,
  onOpenAttachment,
  onOpenLocalAttachment,
  onSaveAttachment,
  onSaveLocalAttachment,
  onLoadAttachmentContent,
  attachmentPreviewRevision,
}: Props) => {
  const lang = useLang();
  const fileInputRef = useRef<HTMLInputElement>();
  const savingAttachmentRef = useRef<string>();
  const [savingAttachmentId, setSavingAttachmentId] = useState<string>();
  const [localError, setLocalError] = useState('');
  const [recordError, setRecordError] = useState('');
  const [editorKind, setEditorKind] = useState<'counterparty' | 'category'>();
  const [viewScope, setViewScope] = useState<string>();
  const [createdCategories, setCreatedCategories] = useState<ApiWalletTransactionCategory[]>([]);
  const [linkedCounterparty, setLinkedCounterparty] = useState<ApiWalletTransactionCounterparty>();
  const [isLinking, setIsLinking] = useState(false);
  const [isRecordBusy, setIsRecordBusy] = useState(false);
  const recordBusyRef = useRef(false);
  const renderedEditorKindRef = useRef<'counterparty' | 'category'>();
  const linkingRef = useRef<string>();
  const scopeKey = `${accountId ?? ''}:${transactionHash ?? ''}:${address ?? ''}`;
  const { runWithAuthorization, authorizationModal } = useMetadataAuthorization(accountId, scopeKey);
  const scopeRef = useRef(scopeKey);
  scopeRef.current = scopeKey;
  const attachmentCount = attachments.length + localAttachments.length;
  const availableCategories = [...categories, ...createdCategories.filter((created) => (
    !categories.some(({ id }) => id === created.id)
  ))];
  const systemCounterparty = counterparty && isWalletSystemCounterparty(counterparty) ? counterparty : undefined;
  const userCounterparty = systemCounterparty ? undefined : counterparty;
  const resolvedCounterparty = userCounterparty && (!linkedCounterparty
    || userCounterparty.updated_at >= linkedCounterparty.updated_at)
    ? userCounterparty
    : linkedCounterparty ?? userCounterparty;
  const displayedCounterparty = resolvedCounterparty ?? systemCounterparty;
  const systemCategory = systemMetadata?.category && isWalletSystemCategory(systemMetadata.category)
    && systemMetadata.category.id === categoryId ? systemMetadata.category : undefined;
  if (editorKind) renderedEditorKindRef.current = editorKind;
  const renderedEditorKind = editorKind ?? renderedEditorKindRef.current;
  const editorRecord = renderedEditorKind === 'counterparty' ? resolvedCounterparty : undefined;

  useEffect(() => {
    setEditorKind(undefined);
    setViewScope(undefined);
    setCreatedCategories([]);
    setLinkedCounterparty(undefined);
    setRecordError('');
    setIsLinking(false);
    setIsRecordBusy(false);
    recordBusyRef.current = false;
    linkingRef.current = undefined;
    return () => {
      if (scopeRef.current === scopeKey) scopeRef.current = '';
    };
  }, [scopeKey]);

  const handleCategoryChange = useLastCallback((value: string) => {
    if (recordBusyRef.current) return;
    if (value === CREATE_RECORD_VALUE) {
      setEditorKind('category');
      return;
    }
    onCategoryChange(value);
  });

  const handleCounterpartyChange = useLastCallback(async (value: string) => {
    if (value === OPEN_RECORD_VALUE && resolvedCounterparty) {
      setViewScope(scopeKey);
      return;
    }
    if (!accountId || !address?.trim() || linkingRef.current || recordBusyRef.current) return;
    setRecordError('');
    if (value === CREATE_RECORD_VALUE || value === EDIT_RECORD_VALUE) {
      setEditorKind('counterparty');
      return;
    }
    const selected = counterparties.find(({ id }) => id === value);
    if (!selected) return;
    const key = scopeKey;
    linkingRef.current = key;
    setIsLinking(true);
    try {
      const saved = await runWithAuthorization(() => callApiWithThrow('saveWalletCounterparty', accountId, {
        id: selected.id,
        name: selected.name,
        addresses: [...new Set([...(selected.addresses ?? []), address.trim()])],
      }));
      notifyWalletMetadataDirectoryChanged({ accountId, record: saved });
      if (scopeRef.current === key) setLinkedCounterparty(saved);
    } catch (cause) {
      if (scopeRef.current !== key || cause instanceof MetadataAuthorizationCancelled) return;
      const code = (cause as { code?: string })?.code;
      setRecordError(lang(code === 'metadata_addresses' ? '$metadata_address_invalid' : '$metadata_local_save_failed'));
    } finally {
      if (linkingRef.current === key) {
        linkingRef.current = undefined;
        setIsLinking(false);
      }
    }
  });

  const handleRecordSaved = useLastCallback((
    record: ApiWalletTransactionCounterparty | ApiWalletTransactionCategory, key: string,
  ) => {
    if (scopeRef.current !== key) return;
    if ('emoji' in record) {
      setCreatedCategories((current) => [...current.filter(({ id }) => id !== record.id), record]);
      onCategoryChange(record.id);
    } else {
      setLinkedCounterparty(resolveAddressCounterparty([record], address));
    }
    setEditorKind(undefined);
    setRecordError('');
  });
  const closeRecordEditor = useLastCallback(() => {
    if (!recordBusyRef.current) setEditorKind(undefined);
  });

  const handleSave = useLastCallback(async (key: string, save: () => void | Promise<void>) => {
    if (savingAttachmentRef.current) return;
    savingAttachmentRef.current = key;
    setSavingAttachmentId(key);
    try {
      await save();
    } catch (error) {
      logDebugError('Transaction attachment save', error);
    } finally {
      savingAttachmentRef.current = undefined;
      setSavingAttachmentId(undefined);
    }
  });
  const handleDescriptionInput = useLastCallback((event: FormEvent<HTMLTextAreaElement>) => {
    onDescriptionChange(event.currentTarget.value);
  });

  const handleAddFiles = useLastCallback((event: ChangeEvent<HTMLInputElement>) => {
    const selectedFiles = Array.from(event.currentTarget.files ?? []);
    event.currentTarget.value = '';
    if (selectedFiles.some((file) => file.size > MAX_ATTACHMENT_BYTES)) {
      setLocalError(lang('$transaction_attachment_too_large'));
      return;
    }
    if (attachmentCount + selectedFiles.length > MAX_ATTACHMENTS) {
      setLocalError(lang('$transaction_attachment_limit'));
      return;
    }
    setLocalError('');
    onAddFiles(selectedFiles);
  });

  return (
    <section className={styles.editor} data-telemetry-block="">
      <div className={styles.heading}>{lang('Transaction details')}</div>
      <div className={styles.group}>
        {(displayedCounterparty || (accountId && address?.trim())) && (
          <div className={buildClassName(styles.field, recordError && styles.fieldWithError)}>
            {systemCounterparty ? (
              <>
                <span className={styles.fieldLabel}>{lang('Counterparty')}</span>
                <span className={styles.systemIdentity} data-telemetry-block="">
                  <CounterpartyAvatar counterparty={systemCounterparty} className={styles.identityAvatar} />
                  <span>{systemCounterparty.name}</span>
                </span>
              </>
            ) : accountId && address?.trim() ? (
              <Dropdown
                withPortal
                withCustomScrollbar
                label={lang('Counterparty')}
                className={styles.categoryControl}
                labelClassName={styles.selectLabel}
                buttonClassName={styles.selectButton}
                selectedValue={resolvedCounterparty?.id ?? ''}
                items={[
                  ...(resolvedCounterparty ? [{
                    value: resolvedCounterparty.id, name: resolvedCounterparty.name,
                    isTelemetryBlocked: true,
                    icon: () => (
                      <CounterpartyAvatar counterparty={resolvedCounterparty} className={styles.identityAvatar} />
                    ),
                  }, { value: OPEN_RECORD_VALUE, name: lang('Open'), fontIcon: 'eye', withDelimiter: true },
                  { value: EDIT_RECORD_VALUE, name: lang('Edit'), fontIcon: 'pen' }] : [
                    { value: '', name: lang('$metadata_unknown_counterparty'), isDisabled: true },
                    ...counterparties.map((contact) => ({
                      value: contact.id, name: contact.name,
                      isTelemetryBlocked: true,
                      icon: () => <CounterpartyAvatar counterparty={contact} className={styles.identityAvatar} />,
                    })),
                    { value: CREATE_RECORD_VALUE, name: lang('$metadata_add_counterparty'),
                      fontIcon: 'plus', withDelimiter: true },
                  ]),
                ]}
                arrow="chevron"
                disabled={isLinking || isRecordBusy}
                isLoading={isLinking}
                onChange={handleCounterpartyChange}
              />
            ) : (
              <>
                <span className={styles.fieldLabel}>{lang('Counterparty')}</span>
                <button
                  type="button"
                  className={styles.counterpartyIdentity}
                  data-telemetry-block=""
                  title={lang('Open')}
                  onClick={() => setViewScope(scopeKey)}
                >
                  <CounterpartyAvatar counterparty={resolvedCounterparty!} className={styles.identityAvatar} />
                  <span>{resolvedCounterparty!.name}</span>
                </button>
              </>
            )}
            {recordError && (
              <span className={buildClassName(inputStyles.label, inputStyles.error, styles.fieldError)} role="alert">
                {recordError}
              </span>
            )}
          </div>
        )}
        {systemCategory ? (
          <div className={styles.field}>
            <span className={styles.fieldLabel}>{lang('Category')}</span>
            <span className={styles.systemIdentity} data-telemetry-block="">
              <MetadataCategoryIcon {...systemCategory} />
              <span>{systemCategory.name}</span>
            </span>
          </div>
        ) : (
          <div className={styles.field}>
            <Dropdown
              withPortal
              withCustomScrollbar
              label={lang('Category')}
              className={styles.categoryControl}
              labelClassName={styles.selectLabel}
              buttonClassName={styles.selectButton}
              selectedValue={categoryId}
              items={[{ value: '', name: lang('$metadata_no_category') }, ...availableCategories.map((category) => ({
                value: category.id, name: category.name, isTelemetryBlocked: true,
                icon: () => <MetadataCategoryIcon {...category} />,
              })), ...(accountId ? [{ value: CREATE_RECORD_VALUE, name: lang('$metadata_add_category'),
                fontIcon: 'plus', withDelimiter: true }] : [])]}
              arrow="chevron"
              disabled={isRecordBusy}
              onChange={handleCategoryChange}
            />
          </div>
        )}
        <label className={buildClassName(styles.field, styles.descriptionField)} htmlFor="transaction-description">
          <span className={styles.fieldLabel}>{lang('Description')}</span>
          <textarea
            id="transaction-description"
            className={styles.descriptionInput}
            value={description}
            maxLength={4000}
            rows={2}
            onInput={handleDescriptionInput}
          />
        </label>

        {attachments.map((attachment) => (
          <AttachmentRow
            key={attachment.id}
            attachment={attachment}
            onOpen={() => onOpenAttachment(attachment)}
            onDownload={() => handleSave(`saved:${attachment.id}`, () => onSaveAttachment(attachment))}
            onRemove={() => onRemoveAttachment(attachment.id)}
            isDownloading={savingAttachmentId === `saved:${attachment.id}`}
            isDownloadDisabled={Boolean(savingAttachmentId)}
            onLoadContent={onLoadAttachmentContent}
            previewRevision={attachmentPreviewRevision}
          />
        ))}
        {localAttachments.map((attachment) => (
          <AttachmentRow
            key={attachment.client_id}
            attachment={attachment}
            onOpen={() => onOpenLocalAttachment(attachment)}
            onDownload={() => handleSave(`local:${attachment.client_id}`, () => onSaveLocalAttachment(attachment))}
            onRemove={() => onRemoveLocalAttachment(attachment.client_id)}
            isDownloading={savingAttachmentId === `local:${attachment.client_id}`}
            isDownloadDisabled={Boolean(savingAttachmentId)}
          />
        ))}
        {attachmentCount < MAX_ATTACHMENTS && (
          <button type="button" className={styles.addAttachment} onClick={() => fileInputRef.current?.click()}>
            <i className="icon-plus" aria-hidden />
            <span>{lang('Add attachments')}</span>
          </button>
        )}
        {localError && (
          <div
            className={buildClassName(inputStyles.label, inputStyles.error, styles.fieldError, styles.attachmentError)}
            role="alert"
          >
            {localError}
          </div>
        )}
      </div>
      {resolvedCounterparty && (
        <CounterpartyDetailsModal
          counterparty={resolvedCounterparty}
          isOpen={viewScope === scopeKey}
          onClose={() => setViewScope(undefined)}
          onEdit={accountId && address?.trim() && !isRecordBusy && !isLinking ? () => {
            setViewScope(undefined);
            setEditorKind('counterparty');
          } : undefined}
        />
      )}
      {renderedEditorKind && accountId && (
        <Modal
          isOpen={Boolean(editorKind)}
          header={(
            <ModalHeader
              title={lang(editorRecord
                ? renderedEditorKind === 'counterparty' ? 'Counterparty' : 'Category'
                : renderedEditorKind === 'counterparty' ? '$metadata_new_counterparty' : '$metadata_new_category')}
              onBackButtonClick={closeRecordEditor}
            />
          )}
          contentClassName={styles.recordEditorPage}
          noBackdropClose={isRecordBusy}
          onClose={closeRecordEditor}
        >
          <MetadataRecordEditor
            key={`${accountId}:${renderedEditorKind}:${editorRecord?.id ?? 'new'}:${transactionHash ?? ''}`
              + `:${address ?? ''}`}
            kind={renderedEditorKind}
            accountId={accountId}
            record={editorRecord}
            initialAddresses={renderedEditorKind === 'counterparty' && address
              ? [...new Set([...(resolvedCounterparty?.addresses ?? []), address.trim()])] : undefined}
            onSaved={(record) => handleRecordSaved(record, scopeKey)}
            onCancel={closeRecordEditor}
            onBusyChange={(isBusy) => {
              if (scopeRef.current !== scopeKey) return;
              recordBusyRef.current = isBusy;
              setIsRecordBusy(isBusy);
            }}
          />
        </Modal>
      )}
      <input
        ref={fileInputRef}
        type="file"
        className={styles.fileInput}
        multiple
        accept={ATTACHMENT_ACCEPT}
        onChange={handleAddFiles}
      />
      {authorizationModal}
    </section>
  );
});

type AttachmentRowProps = {
  attachment: ApiWalletTransactionAttachment | ApiWalletTransactionDraftAttachment;
  onOpen: NoneToVoidFunction;
  onDownload: NoneToVoidFunction;
  onRemove: NoneToVoidFunction;
  isDownloading: boolean;
  isDownloadDisabled: boolean;
  onLoadContent?: AttachmentContentLoader;
  previewRevision?: number;
};

const AttachmentRow = memo(({
  attachment, onOpen, onDownload, onRemove, isDownloading, isDownloadDisabled, onLoadContent, previewRevision,
}: AttachmentRowProps) => {
  const lang = useLang();
  const handleAction = useLastCallback((action: 'download' | 'remove') => {
    if (action === 'download') onDownload();
    else onRemove();
  });

  return (
    <WithContextMenu
      items={[
        {
          value: 'download', name: 'Download', isDisabled: isDownloadDisabled,
          icon: (
            <svg
              className={buildClassName(dropdownStyles.itemIcon, dropdownStyles.fontIcon)}
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.75"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden="true"
            >
              <use href={`${fileIcons}#arrow-down-to-line`} />
            </svg>
          ),
        },
        { value: 'remove', name: 'Remove', fontIcon: 'trash-small', isDangerous: true },
      ]}
      onItemClick={handleAction}
      layout={{ isCenteredHorizontally: false, shouldAvoidNegativePosition: true }}
    >
      {(menuProps, isMenuOpen) => (
        <div
          {...menuProps}
          ref={menuProps.ref as ElementRef<HTMLDivElement>}
          className={styles.attachment}
        >
          <button type="button" className={styles.attachmentMain} onClick={onOpen}>
            <TransactionAttachmentPreview
              attachment={attachment}
              onLoadContent={onLoadContent}
              previewRevision={previewRevision}
            />
            <span className={styles.attachmentInfo}>
              <span>{attachment.file_name}</span>
              <small>{formatAttachmentDetails(attachment)}</small>
            </span>
          </button>
          <button
            type="button"
            className={buildClassName(styles.attachmentMenuButton, isMenuOpen && styles.attachmentMenuButtonActive)}
            aria-label={lang('More actions')}
            aria-haspopup="menu"
            aria-expanded={isMenuOpen}
            title={lang('More actions')}
            onClick={menuProps.onContextMenu}
          >
            {isDownloading ? <Spinner /> : <i className="icon-more" aria-hidden />}
          </button>
        </div>
      )}
    </WithContextMenu>
  );
});

function formatBytes(bytes: number) {
  return bytes < 1024 * 1024
    ? `${Math.max(1, Math.round(bytes / 1024))} KB`
    : `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function formatAttachmentDetails(attachment: { content_type: string; file_name: string; byte_size: number }) {
  const { label } = getTransactionAttachmentFileType(attachment.content_type, attachment.file_name);
  return [label, formatBytes(attachment.byte_size)].filter(Boolean).join(' \u00b7 ');
}
